import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Form, Button, Table, Spinner, Modal } from 'react-bootstrap';
import { showToast as toast } from '../toastManager';
import axios from 'axios';
import '../ChatApp.css';

// ─── Asset picker helpers (mirrors AIAssetContentGenerator.jsx) ──────────────

function getYouTubeThumbnail(url) {
  const m = url?.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=))([^&?/]+)/);
  return m ? `https://img.youtube.com/vi/${m[1]}/mqdefault.jpg` : null;
}

function AssetPickerGroup({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && (
        <div style={{ fontSize: 10, fontWeight: 700, color: '#9aa2ad', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
          {label}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{children}</div>
    </div>
  );
}

function AssetPickerCard({ id, name, previewType, previewUrl, subLabel, selected, onToggle, icon }) {
  const [imgErr, setImgErr] = useState(false);
  const ytThumb = previewType === 'video' ? getYouTubeThumbnail(previewUrl) : null;
  const effectiveUrl = ytThumb || previewUrl;
  const iconFor = { image: '🖼️', video: '🎬', pdf: '📄', contact: '📞', directions: '🗺️', context: '🏢', text: '📃' };

  return (
    <div
      onClick={() => onToggle(id)}
      title={name}
      style={{
        width: 130,
        border: selected ? '2px solid var(--cux-ink, #132848)' : '1px solid #dee2e6',
        borderRadius: 8,
        overflow: 'hidden',
        background: selected ? '#eef2f6' : '#fff',
        cursor: 'pointer',
        flexShrink: 0,
        userSelect: 'none',
      }}
    >
      <div style={{ height: 80, background: '#f0f2f5', position: 'relative', overflow: 'hidden' }}>
        {(previewType === 'image' || previewType === 'video') && effectiveUrl && !imgErr ? (
          <img
            src={effectiveUrl}
            alt={name}
            onError={() => setImgErr(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 28 }}>
            {icon || iconFor[previewType] || '📋'}
          </div>
        )}
        {selected && (
          <div style={{
            position: 'absolute', top: 4, right: 4,
            background: 'var(--cux-ink, #132848)', color: '#fff',
            borderRadius: '50%', width: 18, height: 18,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 700,
          }}>✓</div>
        )}
      </div>
      <div style={{ padding: '5px 7px' }}>
        <div style={{ fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </div>
        {subLabel && <div style={{ fontSize: 10, color: '#6c757d', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subLabel}</div>}
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

const FILTER_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'Image', label: 'Images' },
  { value: 'PDF', label: 'PDFs' },
  { value: 'Video', label: 'Videos' },
  { value: 'Contact', label: 'Contact' },
  { value: 'Get Directions', label: 'Directions' },
  { value: 'Context', label: 'Context' },
];

const EMPTY_DRAFT = {
  group_id: null,
  name: '',
  asset_ids: [],
  include_business_context: false,
  text_content_pages: [],
};

const AssetGroups = ({ ViewedPage, shop_domain, app_url }) => {
  useEffect(() => { if (ViewedPage) ViewedPage(56); }, []); // page-id placeholder

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [groups, setGroups] = useState([]);
  const [assets, setAssets] = useState([]);
  const [storeCtx, setStoreCtx] = useState({ businessContext: null, allTextContent: {} });

  const [editorOpen, setEditorOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filter, setFilter] = useState('All');
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ─── Load data ────────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [assetsRes, ctxRes, groupsRes] = await Promise.all([
        axios.post(`${app_url}/api/assets/get_all`, { shop_domain }, { withCredentials: true }),
        axios.get(`${app_url}/api/store/ai-context`, { withCredentials: true }),
        axios.get(`${app_url}/api/store/asset-groups`, { withCredentials: true }),
      ]);
      if (assetsRes.data.success) setAssets(assetsRes.data.data || []);
      if (ctxRes.data.success) setStoreCtx({
        businessContext: ctxRes.data.data?.businessContext || null,
        allTextContent: ctxRes.data.data?.allTextContent || {},
      });
      if (groupsRes.data.success) setGroups(groupsRes.data.data || []);
    } catch (err) {
      console.error('Error loading Asset Groups data:', err);
      toast.error('Failed to load Asset Groups data');
    } finally {
      setLoading(false);
    }
  }, [shop_domain, app_url]);

  useEffect(() => { if (shop_domain && app_url) loadAll(); }, [shop_domain, app_url, loadAll]);

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const assetById = useMemo(() => {
    const m = new Map();
    for (const a of assets) m.set(String(a._id), a);
    return m;
  }, [assets]);

  const textPagesAvailable = useMemo(
    () => Object.keys(storeCtx.allTextContent || {}).filter(p => {
      const v = storeCtx.allTextContent[p];
      return v && (typeof v === 'string' ? v.trim() : true);
    }),
    [storeCtx.allTextContent]
  );

  const summarizeGroup = (g) => {
    const counts = { Image: 0, Video: 0, PDF: 0, Contact: 0, 'Get Directions': 0, Other: 0 };
    for (const id of g.asset_ids || []) {
      const a = assetById.get(String(id));
      if (!a) continue;
      if (counts[a.type] !== undefined) counts[a.type]++;
      else counts.Other++;
    }
    const parts = [];
    if (counts.Image) parts.push(`${counts.Image} Image${counts.Image > 1 ? 's' : ''}`);
    if (counts.Video) parts.push(`${counts.Video} Video${counts.Video > 1 ? 's' : ''}`);
    if (counts.PDF) parts.push(`${counts.PDF} PDF${counts.PDF > 1 ? 's' : ''}`);
    if (counts.Contact) parts.push(`${counts.Contact} Contact${counts.Contact > 1 ? 's' : ''}`);
    if (counts['Get Directions']) parts.push(`${counts['Get Directions']} Directions`);
    if (g.include_business_context) parts.push('Business Context');
    if (g.text_content_pages?.length) parts.push(`${g.text_content_pages.length} Text Page${g.text_content_pages.length > 1 ? 's' : ''}`);
    return parts.length ? parts.join(' · ') : 'Empty';
  };

  const openNew = () => { setDraft({ ...EMPTY_DRAFT }); setEditorOpen(true); };
  const openEdit = (g) => {
    setDraft({
      group_id: g.group_id,
      name: g.name || '',
      asset_ids: [...(g.asset_ids || [])],
      include_business_context: !!g.include_business_context,
      text_content_pages: [...(g.text_content_pages || [])],
    });
    setEditorOpen(true);
  };

  const closeEditor = () => { setEditorOpen(false); setDraft(EMPTY_DRAFT); setPickerOpen(false); setFilter('All'); };

  // ─── Picker toggles ──────────────────────────────────────────────────────

  const isAssetSelected = (id) => draft.asset_ids.includes(String(id));
  const toggleAsset = (id) => {
    const sid = String(id);
    setDraft(prev => ({
      ...prev,
      asset_ids: prev.asset_ids.includes(sid)
        ? prev.asset_ids.filter(x => x !== sid)
        : [...prev.asset_ids, sid],
    }));
  };
  const toggleBusinessContext = () => setDraft(prev => ({ ...prev, include_business_context: !prev.include_business_context }));
  const toggleTextPage = (page) => setDraft(prev => ({
    ...prev,
    text_content_pages: prev.text_content_pages.includes(page)
      ? prev.text_content_pages.filter(p => p !== page)
      : [...prev.text_content_pages, page],
  }));

  const totalSelected =
    draft.asset_ids.length +
    (draft.include_business_context ? 1 : 0) +
    draft.text_content_pages.length;

  // ─── Save / delete ───────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!draft.name.trim()) { toast.error('Please enter a group name'); return; }
    if (totalSelected === 0) { toast.error('Select at least one asset, business context, or text page'); return; }

    setSaving(true);
    try {
      const res = await axios.post(`${app_url}/api/store/asset-groups`, {
        group_id: draft.group_id,
        name: draft.name.trim(),
        asset_ids: draft.asset_ids,
        include_business_context: draft.include_business_context,
        text_content_pages: draft.text_content_pages,
      }, { withCredentials: true });

      if (res.data.success) {
        toast.success(draft.group_id ? 'Asset group updated' : 'Asset group created');
        setGroups(res.data.data || []);
        closeEditor();
      } else {
        toast.error(res.data.message || 'Save failed');
      }
    } catch (err) {
      console.error('Error saving asset group:', err);
      toast.error(err.response?.data?.message || 'Error saving asset group');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await axios.delete(`${app_url}/api/store/asset-groups/${deleteTarget.group_id}`, { withCredentials: true });
      if (res.data.success) {
        toast.success('Asset group deleted');
        setGroups(res.data.data || []);
      } else {
        toast.error(res.data.message || 'Delete failed');
      }
    } catch (err) {
      console.error('Error deleting asset group:', err);
      toast.error('Error deleting asset group');
    } finally {
      setDeleteTarget(null);
    }
  };

  // ─── Picker render ───────────────────────────────────────────────────────

  const renderPicker = () => {
    const showImages   = filter === 'All' || filter === 'Image';
    const showVideos   = filter === 'All' || filter === 'Video';
    const showPdfs     = filter === 'All' || filter === 'PDF';
    const showContacts = filter === 'All' || filter === 'Contact';
    const showDir      = filter === 'All' || filter === 'Get Directions';
    const showContext  = filter === 'All' || filter === 'Context';

    const imageAssets   = assets.filter(a => a.type === 'Image');
    const videoAssets   = assets.filter(a => a.type === 'Video' || a.type === 'Video Link');
    const pdfAssets     = assets.filter(a => a.type === 'PDF');
    const contactAssets = assets.filter(a => a.type === 'Contact');
    const dirAssets     = assets.filter(a => a.type === 'Get Directions');

    const hasContent =
      (showContext && (storeCtx.businessContext || textPagesAvailable.length > 0)) ||
      (showImages && imageAssets.length) ||
      (showVideos && videoAssets.length) ||
      (showPdfs && pdfAssets.length) ||
      (showContacts && contactAssets.length) ||
      (showDir && dirAssets.length);

    if (!hasContent) {
      return <div style={{ textAlign: 'center', padding: '20px 0', color: '#9aa2ad', fontSize: 13 }}>No assets available for this filter.</div>;
    }

    const filePreviewUrl = (a) =>
      a.publicUrl || a.url ||
      (a.fileName ? `${app_url}/api/assets/file/${a._id}?shop_domain=${shop_domain}` : null);

    return (
      <>
        {showContext && (storeCtx.businessContext || textPagesAvailable.length > 0) && (
          <AssetPickerGroup label="Context">
            {storeCtx.businessContext && (
              <AssetPickerCard
                id="__bc__" name="Business Context" previewType="context" icon="🏢"
                selected={draft.include_business_context}
                onToggle={toggleBusinessContext}
              />
            )}
            {textPagesAvailable.map(page => (
              <AssetPickerCard
                key={`tp-${page}`}
                id={`tp-${page}`}
                name="Text Content"
                subLabel={page.replace(/^https?:\/\//, '').slice(0, 24)}
                previewType="text" icon="📃"
                selected={draft.text_content_pages.includes(page)}
                onToggle={() => toggleTextPage(page)}
              />
            ))}
          </AssetPickerGroup>
        )}

        {showImages && imageAssets.length > 0 && (
          <AssetPickerGroup label="Images">
            {imageAssets.map(a => (
              <AssetPickerCard
                key={a._id} id={a._id} name={a.title}
                previewType="image" previewUrl={filePreviewUrl(a)}
                selected={isAssetSelected(a._id)} onToggle={toggleAsset}
              />
            ))}
          </AssetPickerGroup>
        )}

        {showVideos && videoAssets.length > 0 && (
          <AssetPickerGroup label="Videos">
            {videoAssets.map(a => (
              <AssetPickerCard
                key={a._id} id={a._id} name={a.title}
                previewType="video" previewUrl={a.url}
                selected={isAssetSelected(a._id)} onToggle={toggleAsset}
              />
            ))}
          </AssetPickerGroup>
        )}

        {showPdfs && pdfAssets.length > 0 && (
          <AssetPickerGroup label="PDFs">
            {pdfAssets.map(a => (
              <AssetPickerCard
                key={a._id} id={a._id} name={a.title}
                previewType="pdf"
                selected={isAssetSelected(a._id)} onToggle={toggleAsset}
              />
            ))}
          </AssetPickerGroup>
        )}

        {showContacts && contactAssets.length > 0 && (
          <AssetPickerGroup label="Contact">
            {contactAssets.map(a => {
              // New phone/whatsapp fields take precedence; fall back to legacy
              // `url` + `dial_code` for older rows. Show whichever channels exist.
              const phone = (a.phone_dial_code && a.phone_number)
                ? `${a.phone_dial_code} ${a.phone_number}`
                : (a.dial_code ? `${a.dial_code} ${a.url || ''}` : (a.url || ''));
              const wa = (a.whatsapp_dial_code && a.whatsapp_number)
                ? `WA ${a.whatsapp_dial_code} ${a.whatsapp_number}`
                : null;
              const subLabel = [phone, wa].filter(Boolean).join(' · ');
              return (
                <AssetPickerCard
                  key={a._id} id={a._id} name={a.title}
                  previewType="contact"
                  subLabel={subLabel}
                  selected={isAssetSelected(a._id)} onToggle={toggleAsset}
                />
              );
            })}
          </AssetPickerGroup>
        )}

        {showDir && dirAssets.length > 0 && (
          <AssetPickerGroup label="Get Directions">
            {dirAssets.map(a => (
              <AssetPickerCard
                key={a._id} id={a._id} name={a.title}
                previewType="directions"
                subLabel={a.url}
                selected={isAssetSelected(a._id)} onToggle={toggleAsset}
              />
            ))}
          </AssetPickerGroup>
        )}
      </>
    );
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <Container fluid className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center gap-2">
          <h2 className="mb-0" style={{ color: '#052c65', fontWeight: 700, fontSize: '1.5rem' }}>
            Asset Groups
          </h2>
          {groups.length > 0 && (
            <span className="campaign-tag-new">{groups.length}</span>
          )}
        </div>
        <Button className="sxi_primary_blue_btn" onClick={openNew} disabled={loading}>
          + New Group
        </Button>
      </div>
      <div className="text-muted mb-3" style={{ fontSize: 13 }}>
        Bundle assets so they can be reused as a set on the New Section Page.
      </div>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : groups.length === 0 ? (
        <div className="border rounded bg-white p-4 text-center" style={{ color: '#6B7280' }}>
          No asset groups yet. Click <strong style={{ color: 'var(--cux-ink, #132848)' }}>+ New Group</strong> to create one.
        </div>
      ) : (
        <div className="border rounded bg-white personalization-targeting-table">
          <Table responsive className="mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Contents</th>
                <th>Created</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => (
                <tr key={g.group_id}>
                  <td>{g.group_number}</td>
                  <td><strong>{g.name}</strong></td>
                  <td><small className="text-muted">{summarizeGroup(g)}</small></td>
                  <td><small className="text-muted">{g.created_at ? new Date(g.created_at).toLocaleDateString() : '—'}</small></td>
                  <td className="text-center">
                    <button
                      type="button"
                      className="btn btn-link p-0 me-3"
                      onClick={() => openEdit(g)}
                      style={{ color: '#475467', lineHeight: 0 }}
                      title="Edit"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      onClick={() => setDeleteTarget(g)}
                      style={{ color: '#475467', lineHeight: 0 }}
                      title="Delete"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                        <path d="M10 11v6M14 11v6"/>
                        <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Edit/Create Modal */}
      <Modal show={editorOpen} onHide={closeEditor} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{draft.group_id ? 'Edit Asset Group' : 'New Asset Group'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Group Name</Form.Label>
            <Form.Control
              type="text"
              value={draft.name}
              onChange={(e) => setDraft(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g. Hero Product Bundle"
              maxLength={120}
            />
          </Form.Group>

          <Form.Group className="mb-1">
            <Form.Label className="d-flex justify-content-between align-items-center">
              <span>Assets</span>
              {totalSelected > 0 && (
                <span className="campaign-tag-new">{totalSelected} selected</span>
              )}
            </Form.Label>
            <button
              type="button"
              className="back-button w-100 text-start"
              style={{ padding: '10px 14px' }}
              onClick={() => setPickerOpen(true)}
            >
              {totalSelected > 0 ? 'Edit selection…' : 'Select assets…'}
            </button>
          </Form.Group>

          {totalSelected > 0 && (
            <div className="mt-2 small text-muted">
              {summarizeGroup({
                asset_ids: draft.asset_ids,
                include_business_context: draft.include_business_context,
                text_content_pages: draft.text_content_pages,
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="sxi_primary_blue_btn" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" animation="border" /> : 'Save'}
          </Button>
          <button className="back-button" onClick={closeEditor} disabled={saving}>Cancel</button>
        </Modal.Footer>
      </Modal>

      {/* Picker Modal */}
      <Modal show={pickerOpen} onHide={() => setPickerOpen(false)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            Select Assets {totalSelected > 0 && (
              <span className="campaign-tag-new ms-2">{totalSelected} selected</span>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
            {FILTER_OPTIONS.map(o => (
              <button
                key={o.value}
                type="button"
                onClick={() => setFilter(o.value)}
                style={{
                  padding: '4px 12px', borderRadius: 20,
                  border: filter === o.value ? '2px solid var(--cux-ink, #132848)' : '1px solid #dee2e6',
                  background: filter === o.value ? '#eef2f6' : '#fff',
                  color: filter === o.value ? 'var(--cux-ink, #132848)' : '#495057',
                  fontSize: 12, cursor: 'pointer',
                  fontWeight: filter === o.value ? 600 : 400,
                }}
              >
                {o.label}
              </button>
            ))}
          </div>
          {renderPicker()}
        </Modal.Body>
        <Modal.Footer>
          <Button className="sxi_primary_blue_btn" onClick={() => setPickerOpen(false)}>Done</Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the asset group "{deleteTarget?.name}"?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleDelete}>Delete</Button>
          <button className="back-button" onClick={() => setDeleteTarget(null)}>Cancel</button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AssetGroups;
